// src/components/PrivacyPolicyPage.js
import React from 'react';
import { PublicPage, PageHero } from './public/PublicLayout';

const PrivacyPolicyPage = () => {
    const Section = ({ title, icon, children }) => (
        <section className="mb-10 last:mb-0">
            <h2 className="pub-text pub-line text-2xl sm:text-3xl font-bold mb-6 flex items-center border-b pb-4">
                <i className={`${icon} pub-icon mr-4 text-xl sm:text-2xl`} aria-hidden="true"></i>
                <span>{title}</span>
            </h2>
            <div className="pub-muted leading-relaxed space-y-4">
                {children}
            </div>
        </section>
    );

    const boxClass = 'pub-surface-2 pub-line border p-5 sm:p-6 rounded-lg';
    const boxHeadingClass = 'pub-text font-semibold text-lg mb-2';

    return (
        <PublicPage>
            <PageHero eyebrow="Legal" title="Privacy Policy">
                <p>Your trust is important to us. Here’s how we protect and handle your information.</p>
                <p className="mt-4 text-sm text-slate-300">Last Updated: {new Date("2025-10-01").toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
            </PageHero>

            {/* Privacy Policy Content */}
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
                <div className="pub-surface pub-line border rounded-2xl shadow-lg shadow-black/20 p-6 sm:p-10 lg:p-12">

                    <Section title="Introduction" icon="fas fa-circle-info">
                        <p>
                            LoadMemo ("we," "our," or "us") operates the LoadMemo web platform, the LoadMemo Driver mobile application, and the LoadMemo Import Assistant Chrome Extension (collectively, the "Services"). This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our Services.
                        </p>
                    </Section>

                    <Section title="Information We Collect" icon="fas fa-database">
                         <p>We collect information necessary to provide and improve our Services. This is broken down by how you interact with our platform.</p>
                         <div className={boxClass}>
                           <h3 className={boxHeadingClass}>Web Platform Data (For Fleet Managers & Dispatchers)</h3>
                           <p>Personal information is entered into our secure web platform by authorized company personnel (e.g., dispatchers, fleet managers). This includes:</p>
                           <ul className="list-disc list-inside mt-2 space-y-1">
                               <li><strong className="pub-text">Account Information:</strong> Name, email address, phone number, company affiliation.</li>
                               <li><strong className="pub-text">Driver & Fleet Information:</strong> Driver license details, DOT numbers, vehicle information, and other compliance data.</li>
                               <li><strong className="pub-text">Load Information:</strong> Pickup/delivery locations, customer details, and freight documentation.</li>
                           </ul>
                         </div>
                         <div className={boxClass}>
                           <h3 className={boxHeadingClass}>Mobile App Data (For Drivers)</h3>
                           <p>The LoadMemo Driver mobile app is designed for privacy and simplicity. The app does <strong className="pub-text">NOT</strong> collect, store, or require any personal information from drivers. It operates using only a pre-generated Driver ID assigned via the web platform. The app processes:</p>
                           <ul className="list-disc list-inside mt-2 space-y-1">
                               <li><strong className="pub-text">GPS Data:</strong> Real-time location for active load tracking, used only while a load is in progress.</li>
                               <li><strong className="pub-text">Digital Documentation:</strong> Photos and e-signatures for Proof of Delivery, which are stamped with time and location.</li>
                           </ul>
                         </div>
                         <div className={boxClass}>
                           <h3 className={boxHeadingClass}>Chrome Extension Data</h3>
                           <p>The "LoadMemo Import Assistant" extension helps you import load data. It only handles:</p>
                           <ul className="list-disc list-inside mt-2 space-y-1">
                               <li><strong className="pub-text">Your LoadMemo API Key:</strong> Stored securely and locally in your browser.</li>
                               <li><strong className="pub-text">Web Page Content:</strong> Processed temporarily to extract load data but is never stored by the extension or our servers.</li>
                           </ul>
                         </div>
                    </Section>

                    <Section title="How We Use Your Information" icon="fas fa-gears">
                        <p>We use the information we collect to operate, maintain, and improve our services. This includes:</p>
                        <ul className="list-disc list-inside space-y-2">
                           <li><strong className="pub-text">Service Delivery:</strong> To facilitate load assignments, track shipments, process documentation, and enable communication between dispatchers and drivers.</li>
                           <li><strong className="pub-text">Business Operations:</strong> To generate invoices and earnings reports, maintain load histories, and ensure regulatory compliance.</li>
                           <li><strong className="pub-text">Support and Communication:</strong> To send load notifications, provide customer support, and share important service announcements.</li>
                        </ul>
                    </Section>

                    <Section title="Data Security" icon="fas fa-shield-halved">
                        <p>We implement industry-standard security measures to protect your information, including encryption of data in transit and at rest, secure access controls, and regular security audits. Access to personal data is strictly limited to authorized personnel on a need-to-know basis.</p>
                    </Section>

                    <Section title="Your Rights and Choices" icon="fas fa-user-shield">
                        <p>You have the right to access, update, or request the deletion of your personal data. You can also control location services and communication preferences through the application settings. For any requests regarding your data, please contact us at <a href="mailto:privacy@loadmemo.com" className="pub-link hover:underline">privacy@loadmemo.com</a>.</p>
                    </Section>

                    <Section title="Contact Us" icon="fas fa-envelope">
                        <p>If you have any questions or concerns about this Privacy Policy or our data practices, please don't hesitate to reach out.</p>
                        <div className={`${boxClass} mt-4 flex items-center`}>
                            <span className="pub-soft w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 mr-4">
                                <i className="fas fa-envelope text-xl" aria-hidden="true"></i>
                            </span>
                            <div className="min-w-0">
                                <h4 className="pub-text font-semibold">Email Us</h4>
                                <a href="mailto:privacy@loadmemo.com" className="pub-link hover:underline break-all">privacy@loadmemo.com</a>
                            </div>
                        </div>
                    </Section>

                </div>

                {/* Back to Top Button */}
                <div className="text-center mt-10">
                    <button
                        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                        className="px-8 py-3 pub-btn rounded-lg font-semibold transition"
                    >
                        <i className="fas fa-arrow-up mr-2" aria-hidden="true"></i>Back to Top
                    </button>
                </div>
            </div>
        </PublicPage>
    );
};

export default PrivacyPolicyPage;
